import * as React from 'react'
import dynamic from 'next/dynamic'

import { Step } from './Step'
import { StepGoalLabel, StepsContainer, StyledText } from './styles'

const DefaultCheckmark = dynamic(
  () => import('@thg-commerce/gravity-icons/src/components/Checkmark'),
)

export interface ProgressBarProps {
  activeStep: number
  currentStepCompleted?: boolean
  ariaLabel?: string
  steps: {
    label: string
    icon?: React.ReactElement
  }[]
  progressHeight?: string
  totalSteps?: number
  className?: string
  enableAlternateProgressBar?: boolean
  i18nText?: {
    beforeTotalStepsText?: string
    afterTotalStepsText?: string
  }
  setGoalLabels?: boolean
  isNotRounded?: boolean
}

enum StepsStatuses {
  Completed = 'COMPLETED',
  Processing = 'PROCESSING',
  Incomplete = 'INCOMPLETE',
}

const updateStepsStatuses = (
  stepToUpdate: number,
  currentStep: number,
  currentStepCompleted?: boolean,
): string => {
  if (currentStep === stepToUpdate && !currentStepCompleted) {
    return StepsStatuses.Processing
  }
  if (currentStep > stepToUpdate) {
    return StepsStatuses.Completed
  }

  return StepsStatuses.Incomplete
}

export const ProgressBar = (props: ProgressBarProps) => {
  const activeStep = props.currentStepCompleted
    ? props.activeStep + 1
    : props.activeStep
  const totalSteps = props.totalSteps || props.steps.length

  const progressWidth = `${Math.min(
    (100 / totalSteps) *
      (props.currentStepCompleted ? activeStep - 0.5 : activeStep + 0.5),
    100,
  )}%`

  return (
    <StepsContainer
      enableAlternateProgressBar={props.enableAlternateProgressBar}
      progressWidth={progressWidth}
      progressHeight={props.progressHeight}
      aria-label={props.ariaLabel}
      tabIndex={0}
      className={props.className}
      isRounded={!props.isNotRounded}
    >
      {props.enableAlternateProgressBar && !props.setGoalLabels && (
        <StyledText progressHeight={props.progressHeight}>
          {activeStep ? activeStep : 0} {props.i18nText?.beforeTotalStepsText}{' '}
          {totalSteps} {props.i18nText?.afterTotalStepsText}
        </StyledText>
      )}
      {props.setGoalLabels && (
        <React.Fragment>
          <StepGoalLabel position="start" progressHeight={props.progressHeight}>
            0
          </StepGoalLabel>
          <StepGoalLabel position="end" progressHeight={props.progressHeight}>
            {totalSteps}
          </StepGoalLabel>
        </React.Fragment>
      )}
      {totalSteps > 0 &&
        !props.enableAlternateProgressBar &&
        props.steps.map((step, index) => {
          const stepStatus = updateStepsStatuses(
            index,
            activeStep,
            props.currentStepCompleted,
          )

          const stepIcon =
            activeStep > index ? (
              <DefaultCheckmark data-testid={`checkmark-icon-${index}`} />
            ) : (
              step.icon
            )

          return (
            <Step
              key={`step-${index}`}
              label={step.label}
              icon={stepIcon || index + 1}
              isIncomplete={stepStatus === StepsStatuses.Incomplete}
            />
          )
        })}
    </StepsContainer>
  )
}
